<script setup lang="ts">
import type { BookingStop } from '@/models/advancedJobBooking';

import { computed } from 'vue';

import KPhoneField from '@/components/fields/KPhoneField.vue';
import KTextField from '@/components/fields/KTextField.vue';
import { requiredValidationRule } from '@/helpers/validationRules';

export type ContactModel = Pick<BookingStop, 'note_name' | 'note_phone'>;

export interface Props {
  isWarehouseStop?: boolean;
  readonly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isWarehouseStop: false,
  readonly: false,
});

const model = defineModel<ContactModel>({
  default: () => ({
    note_name: '',
    note_phone: '',
  }),
});

const contactRequiredRules = computed(() =>
  props.isWarehouseStop ? [] : [requiredValidationRule],
);

function handleNoteName(value: string | null | undefined): void {
  model.value = { ...model.value, note_name: value ?? '' };
}

function handleNotePhone(value: string | null | undefined): void {
  model.value = { ...model.value, note_phone: value ?? '' };
}
</script>

<template>
  <fieldset
    :class="$style.stopContactFieldset"
    data-testid="stopContactFieldset"
  >
    <span :class="$style.label">Contact details</span>
    <div :class="$style.fieldsContainer">
      <KTextField
        :model-value="model.note_name"
        label="Name"
        :readonly="props.readonly"
        validate-on="input"
        :rules="contactRequiredRules"
        @update:model-value="handleNoteName"
      />
      <KPhoneField
        :model-value="model.note_phone"
        label="Phone"
        :readonly="props.readonly"
        validate-on="input"
        :rules="contactRequiredRules"
        @update:model-value="handleNotePhone"
      />
    </div>
  </fieldset>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

.stopContactFieldset {
  border: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
  .label {
    font-size: 0.875rem;
    font-weight: 500;
    color: colors.$color-text-primary;
  }
  .fieldsContainer {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 12px;
  }
</style>
